import { CommonModule, formatDate } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../services/session-storage.service';
import { TransportPlanDirectoryMetrics, TransportPlanDirectoryQuery, TransportPlanDirectoryRow } from '../plan-directory/transport-plan-directory.model';
import { TransportPlanningService } from '../transport-planning.service';

interface PlanFilters {
  search: string;
  planNumber: string;
  statuses: string[];
  release: string;
  project: string;
  source: string;
  driver: string;
  vehicle: string;
  origin: string;
  destination: string;
  assignment: string;
  horizonFrom: string;
  horizonTo: string;
  createdFrom: string;
  createdTo: string;
}

@Component({
  selector: 'app-transport-plan-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ButtonModule, IftaLabelModule, InputGroupModule,
    InputGroupAddonModule, InputTextModule, MultiSelectModule, SelectModule,
    TableModule, TagModule, PageShellComponent, PageHeroComponent, PageSearchPanelComponent,
    PageMetricStripComponent, PageDataPanelComponent],
  templateUrl: './transport-plan-list.component.html',
  styleUrls: ['./transport-plan-list.component.css']
})
export class TransportPlanListComponent {
  plans: TransportPlanDirectoryRow[] = [];
  metrics: TransportPlanDirectoryMetrics = this.emptyMetrics();
  totalRecords = 0;
  first = 0;
  isLoading = false;
  message = '';
  filters: PlanFilters = this.emptyFilters();
  expandedPlanIds = new Set<number>();
  readonly statusOptions = ['DRAFT', 'SOLVED', 'STALE', 'APPLIED', 'CANCELLED']
    .map(value => ({ label: this.statusLabel(value), value }));
  readonly assignmentOptions = [
    { label: 'Any assignment', value: '' },
    { label: 'Assigned routes', value: 'ASSIGNED' },
    { label: 'No assigned routes', value: 'UNASSIGNED' }
  ];

  private lastEvent: TableLazyLoadEvent = { first: 0, rows: 25, sortField: 'createdAt', sortOrder: -1 };
  private requestToken = 0;

  constructor(private service: TransportPlanningService, private session: SessionStorageService, private router: Router) {}

  load(event: TableLazyLoadEvent = this.lastEvent): void {
    this.lastEvent = event;
    const token = ++this.requestToken;
    const first = event.first ?? 0;
    const size = event.rows ?? 25;
    const query: TransportPlanDirectoryQuery = {
      owningCompanyId: this.session.getCompany()?.id ?? 0,
      ...this.filters,
      page: Math.floor(first / size), size,
      sortField: typeof event.sortField === 'string' ? event.sortField : 'createdAt',
      sortOrder: event.sortOrder ?? -1
    };
    this.first = first;
    this.isLoading = true;
    this.message = '';
    this.service.searchPlanDirectory(query).subscribe({
      next: page => {
        if (token !== this.requestToken) return;
        this.plans = page.content ?? [];
        this.metrics = page.metrics ?? this.emptyMetrics();
        this.totalRecords = page.totalElements ?? 0;
        this.isLoading = false;
      },
      error: error => {
        if (token !== this.requestToken) return;
        this.message = error?.error?.message || 'Could not load truck plans. Please retry.';
        this.plans = [];
        this.totalRecords = 0;
        this.isLoading = false;
      }
    });
  }

  applyFilters(): void { this.load({ ...this.lastEvent, first: 0 }); }
  clearFilters(): void { this.filters = this.emptyFilters(); this.applyFilters(); }
  refresh(): void { this.load(this.lastEvent); }

  activeFilterCount(): number {
    return Object.values(this.filters).filter(value => Array.isArray(value) ? value.length > 0 : !!value).length;
  }

  summaryMetrics(): PageMetric[] {
    return [
      { label: 'Matching plans', value: this.metrics.total, tone: 'info' },
      { label: 'Draft', value: this.metrics.draft },
      { label: 'Solved', value: this.metrics.solved, tone: 'success' },
      { label: 'Applied', value: this.metrics.applied, tone: 'success' },
      { label: 'Stale', value: this.metrics.stale, tone: 'warn' },
      { label: 'Without assignments', value: this.metrics.unassigned, tone: 'warn' }
    ];
  }

  open(plan: TransportPlanDirectoryRow): void {
    this.router.navigate(['/transport-planning/plans', plan.planId]);
  }

  toggleReleases(planId: number): void {
    if (this.expandedPlanIds.has(planId)) this.expandedPlanIds.delete(planId);
    else this.expandedPlanIds.add(planId);
  }

  date(value?: string): string {
    return value ? formatDate(value, 'dd MMM yyyy HH:mm', 'en-US') : '—';
  }

  statusLabel(value: string): string {
    return value === 'STALE' ? 'Stale' : value.charAt(0) + value.slice(1).toLowerCase();
  }

  statusSeverity(value: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    return value === 'APPLIED' ? 'success' : value === 'SOLVED' ? 'info'
      : value === 'STALE' ? 'warn' : value === 'CANCELLED' ? 'danger' : 'secondary';
  }

  preview(values: string[], limit = 2): string {
    if (!values?.length) return '—';
    return values.slice(0, limit).join(', ') + (values.length > limit ? ` +${values.length - limit} more` : '');
  }

  private emptyFilters(): PlanFilters {
    return { search: '', planNumber: '', statuses: [], release: '', project: '', source: '',
      driver: '', vehicle: '', origin: '', destination: '', assignment: '',
      horizonFrom: '', horizonTo: '', createdFrom: '', createdTo: '' };
  }

  private emptyMetrics(): TransportPlanDirectoryMetrics {
    return { total: 0, draft: 0, solved: 0, applied: 0, cancelled: 0, stale: 0, unassigned: 0 };
  }
}
